import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flame } from 'lucide-react';

import type { MyDayMetric } from '@/api/eos/myDay.api';
import { upsertDailyEntry } from '@/api/scorecards.api';
import { DailyProgressRing } from '@/components/scorecards/DailyProgressRing';
import { useToast } from '@/hooks/useToast';

/**
 * One owned metric with a daily goal on My Day (ADR-048): today's ring, the
 * streak (current + best — a broken streak is demotivating, so the best run
 * stays visible), and for manual metrics a one-tap "log today".
 *
 * The client never decides which day "today" is — the server picks it in
 * the metric's reporting zone and echoes the date it wrote.
 */
export function DailyMetricRow({
  metric,
}: {
  metric: MyDayMetric & { daily: NonNullable<MyDayMetric['daily']> };
}) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [draft, setDraft] = useState('');
  const { daily } = metric;
  const isManual = metric.dataSourceConfig.kind === 'manual';

  const log = useMutation({
    mutationFn: (value: number) => upsertDailyEntry(metric.id, { value }),
    onSuccess: async ({ today }) => {
      setDraft('');
      await qc.invalidateQueries({ queryKey: ['eos', 'my-day'] });
      show(`Logged for ${today}.`, { variant: 'success' });
    },
    onError: () => {
      show('Could not log today’s number.', { variant: 'danger' });
    },
  });

  function submit(e: FormEvent): void {
    e.preventDefault();
    const value = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(value) || log.isPending) return;
    log.mutate(value);
  }

  const streakLabel =
    daily.streak.current > 0
      ? `${String(daily.streak.current)}-day streak${daily.streak.usedGraceDay ? ' (grace day used)' : ''}`
      : daily.streak.longest > 0
        ? 'No streak yet today'
        : 'Start a streak';

  return (
    <li className="flex items-center gap-3">
      <DailyProgressRing progress={daily.progress} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-kw-shark">{metric.title}</p>
        <p className="flex items-center gap-1 text-[11px] text-kw-muted">
          <Flame
            className={`size-3 ${daily.streak.current > 0 ? 'text-kw-warning-text' : 'text-kw-ghost'}`}
          />
          {streakLabel}
          {daily.streak.longest > 0 ? ` · best ${String(daily.streak.longest)}` : ''}
          {daily.provisional ? ' · updating' : ''}
        </p>
      </div>
      {isManual ? (
        <form onSubmit={submit} className="flex items-center gap-1">
          <input
            type="number"
            inputMode="decimal"
            step="any"
            aria-label={`Log today for ${metric.title}`}
            placeholder="Today"
            value={draft}
            disabled={log.isPending}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            className="w-20 rounded-kw-md border border-kw-border bg-white px-2 py-1 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
          <button
            type="submit"
            disabled={draft.trim() === '' || log.isPending}
            className="text-[12px] font-medium text-kw-cerulean hover:underline disabled:text-kw-ghost disabled:no-underline"
          >
            Log
          </button>
        </form>
      ) : null}
    </li>
  );
}
